import request from "../../utils/request";
import { Result } from "../../models/general";

/**
 * Shared filter for /payroll-reports. fromDate/toDate are MONTH selectors (any day inside the
 * month works; the server snaps to whole months and bounds the end month exclusively).
 * `currencyId` / `pageNumber` / `pageSize` were removed: the service never read them.
 */
export interface PayrollReportInput {
    fromDate: string;
    toDate: string;
    departmentIds?: number[];
}

/** One row per (department, month, currency). Never sum across currencies. */
export interface PayrollSummaryView {
    departmentName: string;
    departmentId: number;
    month: string;
    currencyCode: string;
    grossTotal: number;
    deductionsTotal: number;
    netTotal: number;
    /** Money (OvertimePay). Payslips that only carry legacy OT hours contribute 0 — see legacyOvertimeRows. */
    overtimeTotal: number;
    legacyOvertimeRows: number;
    variablePayTotal: number;
    headcount: number;
}

/** One row per payslip (employee × month × currency). */
export interface SalaryRegisterView {
    employeeId: string;
    employeeName: string;
    departmentName: string;
    month: string;
    grossSalary: number;
    deductions: number;
    variablePay: number;
    /** Back-compat alias of overtimePay (money). */
    overtime: number;
    overtimePay: number | null;
    overtimeHours: number;
    adjustments: number;
    netPay: number;
    currencyCode: string;
}

/** One row per (department, month, currency). totalCost = cost to company, not net. */
export interface DepartmentCostView {
    departmentName: string;
    departmentId: number;
    month: string;
    currencyCode: string;
    grossTotal: number;
    additionalEarnings: number;
    employerContributions: number;
    totalCost: number;
    netTotal: number;
    headcount: number;
    /** Back-compat alias of avgGrossSalary. */
    avgSalary: number;
    avgGrossSalary: number;
    avgCostPerEmployee: number;
}

/** Attendance & Overtime Details — one row per (employee, month). */
export interface AttendanceOvertimeView {
    employeeId: string;
    employeeName: string;
    departmentName: string;
    month: string;
    scheduledDays: number;
    presentDays: number;
    /**
     * O4 — days WORKED with no schedule at all (per-day status NonWorkingDay/Holiday + worked hours),
     * counted as present but kept out of `presentDays` so `scheduledDays >= presentDays + absentDays`
     * still holds. Rendered as its own "Unscheduled Present" column.
     */
    unscheduledPresentDays: number;
    absentDays: number;
    leaveDays: number;
    lateArrivals: number;
    lateMinutes: number;
    earlyLeaves: number;
    earlyLeaveMinutes: number;
    totalWorkedHours: number;
    regularOvertimeHours: number;
    premiumOvertimeHours: number;
    overtimePay: number | null;
    currencyCode: string;
    overtimeSource: 'Payroll' | 'Attendance' | string;
    hasPayrollRow: boolean;
}

export async function getPayrollSummary(input: PayrollReportInput): Promise<Result<PayrollSummaryView[]>> {
    const response = await request.post<Result<PayrollSummaryView[]>>("PayrollReports/GetPayrollSummary", input);
    return response.data;
}

export async function getSalaryRegister(input: PayrollReportInput): Promise<Result<SalaryRegisterView[]>> {
    const response = await request.post<Result<SalaryRegisterView[]>>("PayrollReports/GetSalaryRegister", input);
    return response.data;
}

export async function getDepartmentCost(input: PayrollReportInput): Promise<Result<DepartmentCostView[]>> {
    const response = await request.post<Result<DepartmentCostView[]>>("PayrollReports/GetDepartmentCost", input);
    return response.data;
}

export async function getAttendanceOvertime(input: PayrollReportInput): Promise<Result<AttendanceOvertimeView[]>> {
    const response = await request.post<Result<AttendanceOvertimeView[]>>("PayrollReports/GetAttendanceOvertime", input);
    return response.data;
}

/* ── Attendance & Overtime, per day (SPEC docs/reports-revamp/SPEC.md §3) ───────────────────── */

/**
 * The day's shape in the report. `Unscheduled` is a WeekOff/Holiday that nevertheless carries worked
 * hours (the O4 rule), and `Future` is a day inside the period that has not happened yet — it is not
 * an absence and carries zeros. Tone and label come from `components/reports/StatusChip`.
 */
export type AttendanceDayStatus =
    | 'Present' | 'Late' | 'Absent' | 'Leave' | 'HalfDayLeave' | 'SickLeave'
    | 'Holiday' | 'WeekOff' | 'Unscheduled' | 'Future';

/** `Mixed` = the day's shifts disagree; `NotRequired` = the CO-304 approval gate is not armed. */
export type OvertimeApprovalStatus = 'NotRequired' | 'Pending' | 'Approved' | 'Rejected' | 'Mixed';

/** One calendar day of an employee-month. */
export interface AttendanceOvertimeDayView {
    /** ISO datetime at the employee's LOCAL midnight. Read as a calendar date, never via `new Date()`. */
    date: string;
    dayStatus: AttendanceDayStatus | string;
    scheduledHours: number;
    workedHours: number;
    /** Local `HH:mm`, or null when the day has no ended shift. */
    clockIn: string | null;
    clockOut: string | null;
    /** PAYABLE hours — the same carriers payroll reads, after the approval gate. */
    regularOvertimeHours: number;
    premiumOvertimeHours: number;
    /** Display-only: worked, decided against, never paid. */
    rejectedOvertimeHours: number;
    /** Withheld by the approval gate. 0 when the gate is not armed. */
    pendingApprovalHours: number;
    overtimeApprovalStatus: OvertimeApprovalStatus | string;
    /** Rate-based ESTIMATE of what this day's payable overtime is worth. */
    overtimeCost: number;
    lateMinutes: number;
    earlyLeaveMinutes: number;
}

/** One employee-month, with every calendar day of the period inside it. */
export interface AttendanceOvertimeDetailView {
    employeeId: string;
    employeeName: string;
    departmentName: string;
    /** `yyyy-MM`. */
    month: string;
    currencyCode: string;
    hasPayrollRow: boolean;
    overtimeSource: 'Payroll' | 'Attendance' | string;
    /** Null when no gross-pay components resolve for the month; cost is then 0, which is not an error. */
    hourlyRate: number | null;
    regularMultiplier: number;
    premiumMultiplier: number;
    /** `Basic/HoursPerMonth` or `KSA Art.107`. */
    rateBasis: string;
    days: AttendanceOvertimeDayView[];
    scheduledDays: number;
    presentDays: number;
    unscheduledPresentDays: number;
    absentDays: number;
    leaveDays: number;
    lateArrivals: number;
    lateMinutes: number;
    earlyLeaves: number;
    earlyLeaveMinutes: number;
    totalWorkedHours: number;
    regularOvertimeHours: number;
    premiumOvertimeHours: number;
    /** Σ of the days' rate-based cost — an ESTIMATE, not what was paid. */
    overtimeCost: number;
    /** The persisted payslip figure: authoritative for what was PAID. Null when there is no payslip. */
    payslipOvertimePay: number | null;
    payslipRegularOvertimeHours: number | null;
    payslipPremiumOvertimeHours: number | null;
    daysWithOvertime: number;
    pendingApprovalHours: number;
    rejectedOvertimeHours: number;
}

export async function getAttendanceOvertimeDetail(input: PayrollReportInput): Promise<Result<AttendanceOvertimeDetailView[]>> {
    const response = await request.post<Result<AttendanceOvertimeDetailView[]>>("PayrollReports/GetAttendanceOvertimeDetail", input);
    return response.data;
}
